import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { PrincipalTypeSelectors } from "../../../../components/guardian/PrincipalFormComponents";
import { getPrincipalMode } from "../../../../components/guardian/principalHelpers";
import PrincipalPgSection from "./sections/PrincipalPgSection";
import PrincipalAttorneySection from "./sections/PrincipalAttorneySection";
import PrincipalManualSection from "./sections/PrincipalManualSection";

export default function BondPrincipalFormBody({
  index: i,
  guardian,
  guardians,
  cfg,
  keys,
  gVal,
  data,
  updateData,
  globalData,
  errors = {},
  isGuest,
  professionalGuardians,
  attorneys,
  allCities,
  allStates,
  loadingGuardians,
  loadingAttorneys,
  loadingCitiesStates,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  handlers,
  nameSuggestion,
  renderExtraBlocks,
  attorneySearchEnabled,
  attorneySearchInput,
  setAttorneySearchInput,
  debouncedSearchAttorneys,
}) {
  const mode = getPrincipalMode(guardian);
  const ms = cfg.manualStyles || {};

  const {
    updateGuardianAt,
    fetchProfessionalGuardianDetails,
    handleGoogleCallback,
    handleZipCodeChange,
    handleAttorneySelection,
    handleAttorneyHomeAddressChange,
    handleProfessionalGuardianChange,
    handleAttorneyChange,
    formatSSN,
  } = handlers;

  const {
    suggestedPG,
    suggestionDialogOpen,
    getDebouncedCheckName,
    handlePGSuggestionClose,
    handlePGSuggestionDecline,
    handlePGSuggestionConfirm,
    shouldTriggerNameCheck,
  } = nameSuggestion;

  const isGuardianship = cfg.kind === "guardianship";

  const handlePgAutocompleteChange = (newValue) => {
    if (cfg.pgOnChangeUsesUpdateGuardianAt) {
      if (newValue) {
        updateGuardianAt(
          i,
          { professional_guardian: newValue.value, is_new_pg: false },
          guardians
        );
        fetchProfessionalGuardianDetails(i, newValue.value);
      }
      return;
    }
    const updatedGuardians = [...guardians];
    if (newValue) {
      updatedGuardians[i] = {
        ...updatedGuardians[i],
        professional_guardian: newValue.value,
        is_new_pg: false,
      };
      fetchProfessionalGuardianDetails(i, newValue.value);
    } else {
      updatedGuardians[i] = {
        ...updatedGuardians[i],
        professional_guardian: null,
        pg_gia_on_file: false,
        pg_loa_on_file: false,
        is_esignatures_principal: "No",
        credit_report_principal: "",
      };
    }
    updateData({ ...data, guardians: updatedGuardians });
  };

  const pgValue = professionalGuardians.find(
    (pg) => pg.value === guardian.professional_guardian
  );

  const nameOnChange = (inputValue) => {
    const updatedGuardians = [...guardians];
    updatedGuardians[i] = {
      ...updatedGuardians[i],
      [keys.name]: inputValue,
      [keys.citizenship]: "USA",
      ...(isGuardianship ? { country: "USA" } : { [keys.country]: "USA" }),
    };
    updateData({ ...data, guardians: updatedGuardians });
    if (shouldTriggerNameCheck(i, updatedGuardians[i])) {
      getDebouncedCheckName(i)(inputValue);
    }
  };

  const nameSuggestionUi = {
    suggestionDialogOpen,
    suggestedPG,
    handlePGSuggestionClose,
    handlePGSuggestionDecline,
    handlePGSuggestionConfirm,
    nameOnChange,
    getDebouncedCheckName,
    shouldTriggerNameCheck,
  };

  return (
    <Box key={`guardian${String(i)}`} className="grid grid-cols-1 gap-4">
      <Typography variant="h6" className="font-semibold mb-2">
        Principal #{i + 1}
      </Typography>

      <PrincipalTypeSelectors
        guardian={guardian}
        isDisabled={false}
        isGuest={isGuest}
        onProfessionalGuardianChange={(value) =>
          handleProfessionalGuardianChange(i, value, guardian, guardians)
        }
        onAttorneyChange={(value) => handleAttorneyChange(i, value, guardians)}
      />

      {mode === "pg_lookup" && (
        <PrincipalPgSection
          cfg={cfg}
          index={i}
          guardian={guardian}
          guardians={guardians}
          professionalGuardians={professionalGuardians}
          loadingGuardians={loadingGuardians}
          updateGuardianAt={updateGuardianAt}
          selectedPgRecord={pgValue}
          onPgOptionChange={handlePgAutocompleteChange}
          renderExtraBlocks={renderExtraBlocks}
        />
      )}

      {mode === "attorney_lookup" && (
        <>
          <PrincipalAttorneySection
            cfg={cfg}
            index={i}
            guardian={guardian}
            guardians={guardians}
            data={data}
            updateData={updateData}
            errors={errors}
            keys={keys}
            gVal={gVal}
            attorneys={attorneys}
            allCities={allCities}
            allStates={allStates}
            loadingAttorneys={loadingAttorneys}
            loadingCitiesStates={loadingCitiesStates}
            GoogleAutoComplete={GoogleAutoComplete}
            LabeledCreatableSelect={LabeledCreatableSelect}
            handleGoogleCallback={handleGoogleCallback}
            handleZipCodeChange={handleZipCodeChange}
            handleAttorneySelection={handleAttorneySelection}
            handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
            attorneySearchEnabled={attorneySearchEnabled}
            attorneySearchInput={attorneySearchInput}
            setAttorneySearchInput={setAttorneySearchInput}
            debouncedSearchAttorneys={debouncedSearchAttorneys}
          />
          {renderExtraBlocks?.(i, guardian, guardians)}
        </>
      )}

      {mode === "manual" && (
        <PrincipalManualSection
          cfg={cfg}
          index={i}
          guardian={guardian}
          guardians={guardians}
          data={data}
          updateData={updateData}
          globalData={globalData}
          errors={errors}
          keys={keys}
          gVal={gVal}
          nameSuggestionUi={nameSuggestionUi}
          allCities={allCities}
          allStates={allStates}
          loadingCitiesStates={loadingCitiesStates}
          GoogleAutoComplete={GoogleAutoComplete}
          LabeledCreatableSelect={LabeledCreatableSelect}
          handleGoogleCallback={handleGoogleCallback}
          handleZipCodeChange={handleZipCodeChange}
          formatSSN={formatSSN}
          nameSx={ms.name}
          renderExtraBlocks={renderExtraBlocks}
        />
      )}
    </Box>
  );
}

BondPrincipalFormBody.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  cfg: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  gVal: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.object,
  errors: PropTypes.object,
  isGuest: PropTypes.bool,
  professionalGuardians: PropTypes.array,
  attorneys: PropTypes.array,
  allCities: PropTypes.array,
  allStates: PropTypes.array,
  loadingGuardians: PropTypes.bool,
  loadingAttorneys: PropTypes.bool,
  loadingCitiesStates: PropTypes.bool,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  handlers: PropTypes.object.isRequired,
  nameSuggestion: PropTypes.object.isRequired,
  renderExtraBlocks: PropTypes.func,
};
